/**
 * frontend/src/pages/Dashboard.jsx
 * ==================================
 * Manager dashboard home — aggregated stats + partner leaderboard.
 *
 * Queries:
 *   GET /api/dashboard/stats   — KPI cards
 *   GET /api/dashboard/partners — leaderboard table
 *   GET /api/sync/status       — last sync info
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { dashboardApi, syncApi } from '../lib/api'
import { format, subDays } from 'date-fns'

// ─── Helpers ──────────────────────────────────────────────────────────────

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  }).format(amount ?? 0)
}

function KpiCard({ label, value, sub }) {
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
      <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
        {label}
      </div>
      <div className="text-3xl font-bold text-white tabular-nums">{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
    </div>
  )
}

// ─── Date range presets ───────────────────────────────────────────────────

const PRESETS = [
  { label: '7 days',  days: 7  },
  { label: '30 days', days: 30 },
  { label: '90 days', days: 90 },
]

// ─── Component ────────────────────────────────────────────────────────────

export default function Dashboard() {
  const qc = useQueryClient()
  const [activeDays, setActiveDays] = useState(30)
  const [syncFeedback, setSyncFeedback] = useState(null)

  const today = format(new Date(), 'yyyy-MM-dd')
  const fromDate = format(subDays(new Date(), activeDays), 'yyyy-MM-dd')

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboard-stats', fromDate, today],
    queryFn: () =>
      dashboardApi
        .stats({ from_date: fromDate, to_date: today })
        .then((r) => r.data),
  })

  const { data: partners, isLoading: partnersLoading } = useQuery({
    queryKey: ['dashboard-partners', fromDate, today],
    queryFn: () =>
      dashboardApi
        .partners({ from_date: fromDate, to_date: today, limit: 10 })
        .then((r) => r.data),
  })

  const syncMutation = useMutation({
    mutationFn: () => syncApi.trigger({ sync_type: 'full' }),
    onSuccess: (res) => {
      setSyncFeedback({ type: 'success', msg: 'Sync started — data will update shortly.' })
      setTimeout(() => {
        qc.invalidateQueries()
        setSyncFeedback(null)
      }, 5000)
    },
    onError: (err) => {
      setSyncFeedback({
        type: 'error',
        msg: err.response?.data?.detail || 'Sync failed. Check C7 credentials in Settings.',
      })
    },
  })

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* ── Header ── */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-white">Dashboard</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Referral partner performance overview
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* Date preset tabs */}
          <div className="flex bg-gray-900 border border-gray-800 rounded-lg p-0.5">
            {PRESETS.map(({ label, days }) => (
              <button
                key={days}
                onClick={() => setActiveDays(days)}
                className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                  activeDays === days
                    ? 'bg-violet-700 text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Sync button */}
          <button
            onClick={() => syncMutation.mutate()}
            disabled={syncMutation.isPending}
            className="px-4 py-2 bg-violet-700 hover:bg-violet-600 disabled:opacity-50 text-white text-sm rounded-lg font-medium transition-colors"
          >
            {syncMutation.isPending ? 'Syncing…' : '↻ Sync C7'}
          </button>
        </div>
      </div>

      {/* Sync feedback */}
      {syncFeedback && (
        <div
          className={`mb-5 px-4 py-3 rounded-lg text-sm ${
            syncFeedback.type === 'error'
              ? 'bg-red-900/40 text-red-300 border border-red-800'
              : 'bg-green-900/40 text-green-300 border border-green-800'
          }`}
        >
          {syncFeedback.msg}
        </div>
      )}

      {/* ── KPI cards ── */}
      {statsLoading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-gray-900 border border-gray-800 rounded-xl p-5 h-24 animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <KpiCard
            label="Total Check-Ins"
            value={stats?.total_checkins?.toLocaleString() ?? '—'}
            sub={`${stats?.total_guests?.toLocaleString() ?? 0} guests`}
          />
          <KpiCard
            label="Reservations"
            value={stats?.total_reservations?.toLocaleString() ?? '—'}
            sub="from Commerce7"
          />
          <KpiCard
            label="Orders"
            value={stats?.total_orders?.toLocaleString() ?? '—'}
            sub="POS + online"
          />
          <KpiCard
            label="Revenue"
            value={formatCurrency(stats?.total_revenue)}
            sub="attributed orders"
          />
        </div>
      )}

      {/* ── Partner leaderboard ── */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-800 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-white">Partner Leaderboard</h2>
          <span className="text-xs text-gray-500">
            Last {activeDays} days
          </span>
        </div>

        {partnersLoading ? (
          <div className="p-5 space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-10 bg-gray-800 rounded animate-pulse" />
            ))}
          </div>
        ) : !partners?.length ? (
          <div className="px-5 py-10 text-center text-gray-500 text-sm">
            No partner data yet.{' '}
            <a href="/partners" className="text-violet-400 hover:underline">
              Add partners
            </a>{' '}
            or run a sync.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-500 uppercase tracking-wider">
                <th className="px-5 py-3 text-left font-medium">Partner</th>
                <th className="px-4 py-3 text-right font-medium">Check-Ins</th>
                <th className="px-4 py-3 text-right font-medium">Guests</th>
                <th className="px-4 py-3 text-right font-medium">Reservations</th>
                <th className="px-4 py-3 text-right font-medium">Orders</th>
                <th className="px-5 py-3 text-right font-medium">Revenue</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {partners.map((p, i) => (
                <tr key={p.partner_id} className="hover:bg-gray-800/50 transition-colors">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600 tabular-nums text-xs w-4">
                        {i + 1}
                      </span>
                      <div>
                        <div className="text-white font-medium">{p.partner_name}</div>
                        <div className="text-xs text-gray-500 capitalize">
                          {p.partner_type}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right text-gray-300 tabular-nums">
                    {p.total_checkins.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-300 tabular-nums">
                    {p.total_guests.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-300 tabular-nums">
                    {p.total_reservations.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-300 tabular-nums">
                    {p.total_orders.toLocaleString()}
                  </td>
                  <td className="px-5 py-3 text-right font-medium text-green-400 tabular-nums">
                    {formatCurrency(p.total_revenue)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Last sync timestamp ── */}
      {stats?.last_sync_at && (
        <p className="mt-3 text-xs text-gray-600 text-right">
          Last synced:{' '}
          {format(new Date(stats.last_sync_at), 'MMM d, yyyy h:mm a')}
        </p>
      )}
    </div>
  )
}
